import { Link } from "react-router-dom";
import { BookOpen, CheckCircle2, Clock, Plus, UserCheck, XCircle } from "lucide-react";
import { PageLoader } from "#/components/common/PageLoader";
import { ErrorState } from "#/components/common/ErrorState";
import { useMyTrainingRequests, usePublishedPrograms, useTrainingInbox } from "../hooks";
import { formatUsername } from "../utils";
import type { TrainingRequestStatus } from "../types";

function StatusBadge({ status }: { status: TrainingRequestStatus }) {
  const map: Record<TrainingRequestStatus, string> = {
    DRAFT:           "bg-slate-100 text-slate-700",
    SUBMITTED:       "bg-amber-100 text-amber-800",
    APPROVED:        "bg-emerald-100 text-emerald-800",
    REJECTED:        "bg-red-100 text-red-700",
    CANCELLED:       "bg-slate-200 text-slate-600",
    COMPLETED:       "bg-indigo-100 text-indigo-800",
  };
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${map[status] ?? "bg-slate-100 text-slate-600"}`}>
      {status.replace(/_/g, " ")}
    </span>
  );
}

function fmt(v?: string | null) {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return v; }
}

function fmtDateTime(v?: string | null) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return v; }
}

export default function TrainingDashboardPage() {
  const mine      = useMyTrainingRequests(0, 5);
  const inbox     = useTrainingInbox(0, 5);
  const programsQuery = usePublishedPrograms();

  if (mine.isLoading || inbox.isLoading) return <PageLoader label="Loading training…" />;
  if (mine.isError) return <ErrorState title="Failed to load training requests" message={String(mine.error)} />;

  const publishedPrograms = programsQuery.data?.content ?? [];

  const myItems    = mine.data?.content  ?? [];
  const inboxItems = inbox.data?.content ?? [];
  const inboxTotal = inbox.data?.totalElements ?? 0;

  const statuses: TrainingRequestStatus[] = ["SUBMITTED", "APPROVED", "REJECTED", "COMPLETED"];
  const counts = statuses.reduce<Record<string, number>>((acc, s) => {
    acc[s] = myItems.filter((r) => r.status === s).length;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Training</h1>
        </div>
        <Link
          to="/hr/training/new"
          className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
        >
          <Plus className="h-4 w-4" /> New Request
        </Link>
      </div>

      {/* Published programs banner */}
      {publishedPrograms.length > 0 && (
        <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-5">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="h-5 w-5 text-amber-600 shrink-0" />
            <h2 className="font-black text-amber-900">
              {publishedPrograms.length} Active Training Program{publishedPrograms.length !== 1 ? "s" : ""}
            </h2>
          </div>
          <div className="space-y-2">
            {publishedPrograms.map((p) => (
              <Link
                key={p.id}
                to={`/hr/training/programs/${p.id}`}
                className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-white px-4 py-3 hover:bg-amber-50/60 transition-colors"
              >
                <div>
                  <p className="font-semibold text-slate-800 text-sm">{p.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {p.categoryName && `${p.categoryName} · `}
                    {p.startDate ?? "Date TBC"}
                    {p.venue ? ` · ${p.venue}` : ""}
                    {p.enrollmentCount != null ? ` · ${p.enrollmentCount} enrolled` : ""}
                  </p>
                </div>
                <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-bold text-emerald-800">
                  PUBLISHED
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Quick stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: "Pending",   value: counts["SUBMITTED"] ?? 0, icon: Clock,        color: "text-amber-600   bg-amber-50   border-amber-200" },
          { label: "Approved",  value: counts["APPROVED"]  ?? 0, icon: CheckCircle2, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
          { label: "Rejected",  value: counts["REJECTED"]  ?? 0, icon: XCircle,      color: "text-red-600     bg-red-50     border-red-200" },
          { label: "Completed", value: counts["COMPLETED"] ?? 0, icon: BookOpen,     color: "text-indigo-700  bg-indigo-50  border-indigo-200" },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className={`rounded-2xl border p-4 ${color}`}>
            <Icon className="h-5 w-5 mb-1" />
            <p className="text-2xl font-black">{value}</p>
            <p className="text-xs font-semibold mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* My recent requests */}
        <div className="rounded-2xl border bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between border-b px-5 py-3">
            <h2 className="font-bold text-slate-800">My Requests</h2>
            <Link to="/hr/training/mine" className="text-xs font-semibold text-emerald-700 hover:underline">
              View all →
            </Link>
          </div>
          {myItems.length === 0 ? (
            <p className="px-5 py-6 text-sm text-slate-400">No training requests yet.</p>
          ) : (
            <div className="divide-y">
              {myItems.map((r) => (
                <Link
                  key={r.id}
                  to={`/hr/training/${r.id}`}
                  className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-slate-50 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-800">{r.trainingTitle}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {r.providerName ? `${r.providerName} · ` : ""}
                      {fmt(r.trainingStartDate)}
                    </p>
                    {r.status === "SUBMITTED" && (
                      <p className="mt-1 flex items-center gap-1 text-xs font-semibold text-amber-700">
                        <UserCheck className="h-3 w-3 shrink-0" />
                        {r.currentStepName ? `${r.currentStepName} — ` : ""}
                        {r.currentApproverUsername ? formatUsername(r.currentApproverUsername) : "awaiting assignment"}
                      </p>
                    )}
                  </div>
                  <StatusBadge status={r.status} />
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Approval inbox */}
        <div className="rounded-2xl border bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between border-b px-5 py-3">
            <h2 className="font-bold text-slate-800">
              Approval Inbox
              {inboxTotal > 0 && (
                <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-700">
                  {inboxTotal}
                </span>
              )}
            </h2>
            <Link to="/hr/training/inbox" className="text-xs font-semibold text-emerald-700 hover:underline">
              View all →
            </Link>
          </div>
          {inboxItems.length === 0 ? (
            <p className="px-5 py-6 text-sm text-slate-400">No pending approvals.</p>
          ) : (
            <div className="divide-y">
              {inboxItems.map((r) => (
                <Link
                  key={r.id}
                  to={`/hr/training/${r.id}`}
                  className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-slate-50 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-800">{r.trainingTitle}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {r.employeeName ?? "Unknown"} · {fmtDateTime(r.submittedAt)}
                    </p>
                  </div>
                  <StatusBadge status={r.status} />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
